import { Injectable, TemplateRef } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ScreenSizes } from '../classes/screen-sizes.class';
import { ContextMenuOptions } from '../components/context-menu-global/context-menu-global.component';
import { sleep } from '../lib/sleep';
import { enableBodyScroll } from '../lib/toggle-body-scroll';
import { AnalyticsService } from './analytics.service';

export interface ContextMenuComponent {
  position: string;
  verticalPosition: string;
  options: ContextMenuOptions;
  name: string;
  contextHoverIdentifier: string;
  component: { close: () => void };
  clientEl: HTMLElement;
  template: TemplateRef<unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class ContextMenuService {
  private _context: Subject<ContextMenuComponent> = new BehaviorSubject<ContextMenuComponent>(undefined);
  public readonly onContextOpen: Observable<ContextMenuComponent> = this._context.asObservable();
  public _contextContentHover: Subject<boolean> = new BehaviorSubject<boolean>(undefined);
  public readonly onContextContentHover: Observable<boolean> = this._contextContentHover.asObservable();
  public activeComponent: ContextMenuComponent;
  public hoveringOverContextContent: boolean;

  constructor(private analytics: AnalyticsService) {}

  // Should only be used from the ContextMenuComponent
  public async showContext(component: typeof this.activeComponent): Promise<boolean> {
    if (this.activeComponent) {
      const justClose = this.activeComponent.clientEl === component.clientEl;
      this.activeComponent.component.close();
      await sleep(0);
      if (justClose) {
        // If we clicked on the same element again, we just close the current one
        return false;
      }
    }
    if (component.name) this.analytics.track('open_context:' + component.name, false);
    this.activeComponent = component;
    this._context.next(component);
    return true;
  }
  // Should only be used from the ContextMenuComponent
  public hideContext() {
    this.activeComponent = undefined;
    this._context.next(undefined);
    if (ScreenSizes.screenSize() === 'mobile') enableBodyScroll();
  }

  public closeContext() {
    this.activeComponent?.component.close();
  }
}
